import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  #gridImageSizesObserver = new MutationObserver(() => this.#syncGridImageSizes());

  /** Keep eager card images sharp when the native mobile grid changes density. */
  #syncGridImageSizes() {
    const grid = this.querySelector('[ref="grid"]');
    if (!grid) return;

    const singleColumn = grid.getAttribute('product-grid-view') === 'mobile-single' ||
      grid.classList.contains('product-grid-mobile--large');
    const mobileFullWidth = '(max-width: 749px) 100vw, ';
    for (const image of grid.querySelectorAll('.card-gallery img[loading="eager"][sizes]')) {
      const current = image.getAttribute('sizes') || '';
      const baseSizes = current.startsWith(mobileFullWidth) ? current.slice(mobileFullWidth.length) : current;
      const sizes = singleColumn ? mobileFullWidth + baseSizes : baseSizes;
      if (current !== sizes) image.setAttribute('sizes', sizes);
    }
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');
    this.#syncGridImageSizes();
    // Observe only the native view attribute and inserted/replaced cards, not sizes.
    this.#gridImageSizesObserver.observe(this, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['product-grid-view'],
    });
  }

  updatedCallback() {
    super.updatedCallback();
    this.#syncGridImageSizes();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
    this.#gridImageSizesObserver.disconnect();
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
